
<script setup>

  import { ref, computed } from 'vue';

  import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';

  import { ArrowRightCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline';

  import { parseImageName } from '../utils.js';

  import horizontalPaths from '../poster/horizontal/*.png';

  import verticalPaths from '../poster/vertical/*.png';

  import squarePaths from '../poster/square/*.png';

  const sets = { horizontal: horizontalPaths, vertical: verticalPaths, square: squarePaths };

  const name = ref( null );

  const type = ref( null );

  const paths = computed( () => sets[ type.value ] ? Object.values( sets[ type.value ] ) : [] );

  const selection = ref( null );

  const isOpen = ref( false );

  const promise = ref( null );

  let promiseResolve;

  function select( nameValue, typeValue ) {

    name.value = nameValue;

    type.value = typeValue;

    isOpen.value = true;

    return promise.value = new Promise( resolve => promiseResolve = resolve );

  }

  function resolve() {
    
    isOpen.value = false;

    if ( promiseResolve ) promiseResolve( selection.value );

  }

  function reject() {
    
    isOpen.value = false;

    if ( promiseResolve ) promiseResolve( null );

  }

  defineExpose( { select, resolve } );

</script>

<template>

  <TransitionRoot appear :show="isOpen" as="template">

    <Dialog as="div" @close="reject" class="fixed inset-0 overflow-hidden z-20 drop-shadow-[0_0_15px_rgba(0,0,0,0.5)]">

      <div class="absolute inset-y-0 right-0 overflow-hidden">

        <TransitionChild

          as="template"

          enter="transition transform duration-300 ease-out"

          enter-from="translate-x-full"

          enter-to="translate-x-0"

          leave="transition transform duration-200 ease-in"

          leave-from="translate-x-0"

          leave-to="translate-x-full">

          <DialogPanel class="flex flex-col h-view py-8 mt-header text-white bg-black/90">
            
            <div class="flex items-center justify-between gap-5 px-8">

              <div>

                <DialogTitle as="h3" class="font-bold text-2xl">{{ name }} Selection</DialogTitle>

                <p class="mt-2">Select an image below for the {{ name }}.</p>

              </div>

              <button type="button" @click="reject" class="group flex items-center gap-2">

                <div class="text-sm group-focus-visible:underline group-hover:underline">Close</div>

                <XCircleIcon class="w-8" />
              
              </button>

            </div>

            <div class="flex flex-col min-h-0 grow mt-5 pr-8">

              <div class="grid sm:grid-cols-3 gap-4 max-w-2xl min-h-0 pl-8 pr-4 grow overflow-y-auto">

                <label v-for="path in paths" class="grid grid-rows-[auto_1fr] grid-cols-[auto_1fr] gap-3 pb-3">
                
                  <input type="radio" name="selection" :value="path" v-model="selection" class="peer self-center" />

                  <img class="order-first col-span-2 border-4 border-transparent peer-checked:border-white"
                  
                    :src="path" alt="" draggable="false" />

                  <div class="self-center break-words text-xs">{{ parseImageName( path ) }}</div>
                
                </label>

              </div>

              <div class="flex flex-wrap items-center justify-between mt-5 pl-8">

                <button
                  
                  type="button" @click="reject"

                  class="inline-flex items-center gap-4 px-6 py-4 text-white font-bold text-white bg-neutral-500 transition-colors
                  
                    hover:bg-neutral-600 focus-visible:bg-neutral-800 hover:underline focus-visible:underline">
                  
                  <XCircleIcon class="w-8" />

                  <span>Cancel</span>
                
                </button>

                <button
                  
                  type="button" @click="resolve"

                  class="inline-flex items-center gap-4 px-6 py-4 text-white font-bold text-white bg-green-700 transition-colors
                  
                    hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline">
                  
                  <span>Done</span>

                  <ArrowRightCircleIcon class="w-8" />
                
                </button>

              </div>

            </div>

          </DialogPanel>

        </TransitionChild>

      </div>

    </Dialog>

  </TransitionRoot>

</template>
